// frontend/src/components/booking-management/BookingView.js

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from '../../axiosConfig';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useTheme } from '../../contexts/ThemeContext';
import { usePreferences } from '../../contexts/PreferenceContext';
import translationService from '../../services/translationService';
import { isAdmin } from '../../utils/roleUtils';
import { List, Calendar, CheckCircle2, AlertTriangle, XCircle, Plus } from 'lucide-react';
import ConfirmDialogShell from '../dialogs/ConfirmDialogShell';
import { formatDate, formatTimeString } from '../../utils/dateTimeFormatter';
import { useAdminBookingWebSocket } from '../../hooks/useAdminBookingWebSocket';
import { useRiderBookingWebSocket } from '../../hooks/useRiderBookingWebSocket';

// Import sub-components
import BookingFilters from './BookingFilters';
import TripGroupedView from './TripGroupedView';
import BookingListPanel from './BookingListPanel';
import ActiveDriversPanel from './ActiveDriversPanel';

const isEnabledFlag = (value) =>
  value === true || value === 'true' || value === 1 || value === '1';

const normalizeTableText = (value) => String(value ?? '').trim();

/**
 * Unified BookingView Component
 * - Users: See their own bookings with simple controls
 * - Admins: See all entity bookings with management tools
 * - Both: Can switch between List and Trip-grouped views
 * - Full multi-language support with batch translation
 */
const BookingView = ({
  userRole,
  entityId,
  bookingWindowDays,
  refreshKey = 0,
  renderCreateForm = null
}) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { background, text, cn } = useTheme();
  const { entitySettings, userPreferences } = usePreferences();
  const isAdminUser = isAdmin(userRole);
  const handleViewTrip = useCallback((tripId) => {
    if (!tripId) {
      return;
    }

    navigate('/trips', { state: { tripId } });
  }, [navigate]);
  const preferredDateFormat =
    userPreferences?.date_format ||
    userPreferences?.dateFormat ||
    'MM/DD/YYYY';
  const preferredTimeFormat =
    userPreferences?.time_format ||
    userPreferences?.timeFormat ||
    '12h';
  const preferredLanguage =
    userPreferences?.language ||
    i18n.language ||
    'en';

  // View state - riders can only see list view, admins can toggle between list, trip, and create
  const [viewMode, setViewMode] = useState('list'); // 'list', 'trip', or 'create'
  const [bookings, setBookings] = useState([]);
  const [trips, setTrips] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [translationsLoading, setTranslationsLoading] = useState(false);
  const [totalUnfilteredCount, setTotalUnfilteredCount] = useState(0); // For filter threshold check
  const [pendingAction, setPendingAction] = useState(null); // Tracks the action awaiting confirmation
  const [actionProcessing, setActionProcessing] = useState(false); // Prevents duplicate submissions while API runs
  const isConfirmAction = pendingAction?.type === 'confirm';

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(50);
  const [totalPages, setTotalPages] = useState(1);

  // Filter state - Initialize with status smart default, dates will be set by BookingFilters
  const [filters, setFilters] = useState({
    status: 'all', // Smart default for bookings of all status.
    routeId: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    search: ''
  });

  // Track if filters have been fully initialized (including dates)
  const [filtersInitialized, setFiltersInitialized] = useState(false);


  // Multi-language enabled check
  const multiLanguageEnabled = entitySettings?.multi_language_enabled === true;
  const entityDefaultLanguage = entitySettings?.entity_default_language || 'en';
  const shouldTranslateContent = multiLanguageEnabled && i18n.language !== entityDefaultLanguage;

  // Check if booking verification is enabled for this entity
  const rawVerificationEnabled = entitySettings?.booking_additional_verification_enabled;
  const bookingVerificationEnabled = rawVerificationEnabled === true ||
    rawVerificationEnabled === 'true' ||
    rawVerificationEnabled === 1 ||
    rawVerificationEnabled === '1';
  const additionalVerificationPrompt = entitySettings?.booking_additional_verification_prompt?.trim() || '';
  const [translatedVerificationPrompt, setTranslatedVerificationPrompt] = useState(additionalVerificationPrompt);

  // Track latest list-view fetch so stale responses cannot overwrite new data
  const latestBookingRequestRef = useRef(0);
  const latestTripRequestRef = useRef(0);

  // Track newly received booking IDs (via WebSocket) to show "New" badge
  const [newBookingIds, setNewBookingIds] = useState(new Set());
  const [activeDriversRefreshKey, setActiveDriversRefreshKey] = useState(0);

  // Ensure non-admin users are always in list view
  useEffect(() => {
    if (!isAdminUser && viewMode !== 'list') {
      setViewMode('list');
    }
  }, [isAdminUser, viewMode]);


  // Fetch routes for admin filter
  useEffect(() => {
    if (isAdminUser) {
      fetchRoutes();
    }
  }, [isAdminUser, entityId, i18n.language, multiLanguageEnabled, entityDefaultLanguage]);

  // Fetch translated verification prompt when language changes
  useEffect(() => {
    const fetchVerificationPromptTranslation = async () => {
      if (!bookingVerificationEnabled || !additionalVerificationPrompt || !shouldTranslateContent) {
        setTranslatedVerificationPrompt(additionalVerificationPrompt);
        return;
      }

      if (!entityId) {
        setTranslatedVerificationPrompt(additionalVerificationPrompt);
        return;
      }

      // Need the preference record ID to fetch translations
      const preferenceMetadata = entitySettings?.preferenceMetadata?.booking_additional_verification_prompt;

      if (!preferenceMetadata?.id) {
        setTranslatedVerificationPrompt(additionalVerificationPrompt);
        return;
      }

      try {
        const translatedValue = await translationService.getTranslatedContent(
          entityId,
          'entity_preference',
          preferenceMetadata.id,
          'preference_value',
          i18n.language,
          additionalVerificationPrompt
        );
        setTranslatedVerificationPrompt(translatedValue?.content || additionalVerificationPrompt);
      } catch (error) {
        console.warn('Failed to fetch verification prompt translation:', error);
        setTranslatedVerificationPrompt(additionalVerificationPrompt);
      }
    };

    fetchVerificationPromptTranslation();
  }, [
    bookingVerificationEnabled,
    additionalVerificationPrompt,
    shouldTranslateContent,
    entitySettings,
    entityId,
    i18n.language
  ]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters, viewMode]);

  // Fetch bookings when filters, language, view, page, or refreshKey changes (but only after initialization)
  useEffect(() => {
    // Bail out until entity context finishes loading to avoid hitting /undefined.
    if (!entityId) {
      return;
    }

    if (filtersInitialized) {
      if (viewMode === 'list') {
        fetchBookings();
      } else {
        fetchTripsGrouped();
      }
    }
  }, [entityId, filters, i18n.language, viewMode, currentPage, filtersInitialized, refreshKey]);

  // Store current viewMode in ref for WebSocket callback (avoids stale closure)
  const viewModeRef = useRef(viewMode);
  useEffect(() => {
    viewModeRef.current = viewMode;
  }, [viewMode]);

  // Real-time booking updates for admins via WebSocket
  const handleBookingEvent = useCallback((event) => {
    if (event.type === 'driver_availability_changed') {
      setActiveDriversRefreshKey(prev => prev + 1);
      return;
    }

    if (event.type === 'booking_created' && event.bookingId) {
      // Show toast notification for new bookings
      toast.info(t('newBookingReceived'), { autoClose: 3000 });

      // Track this booking as "new" for badge display
      setNewBookingIds(prev => new Set([...prev, event.bookingId]));

      // Auto-clear the "New" badge after 30 seconds
      setTimeout(() => {
        setNewBookingIds(prev => {
          const updated = new Set(prev);
          updated.delete(event.bookingId);
          return updated;
        });
      }, 30000);
    }
    // For any booking event (created, updated, canceled), trigger a refetch
    // Force a refetch by briefly toggling a state that triggers the useEffect
    setFiltersInitialized(false);
    setTimeout(() => setFiltersInitialized(true), 0);
  }, [t]);

  // Only connect WebSocket for admin users not in create mode
  const shouldConnectWs = isAdminUser && viewMode !== 'create' && !!entityId;
  useAdminBookingWebSocket(entityId, shouldConnectWs, handleBookingEvent);

  // Real-time booking status updates for riders (non-admin users) via WebSocket
  const handleRiderBookingEvent = useCallback((event) => {
    if (event.type === 'booking_status_changed') {
      // Show appropriate toast based on new status
      const message = event.newStatus === 'confirmed'
        ? t('bookingConfirmedNotification')
        : event.newStatus === 'waitlist'
          ? t('bookingCreatedWaitlist')
        : event.newStatus === 'canceled'
          ? t('bookingCanceledNotification')
          : t('bookingStatusChangedNotification');

      toast.info(message, { autoClose: 5000 });

      // Trigger a refetch to update the booking list
      setFiltersInitialized(false);
      setTimeout(() => setFiltersInitialized(true), 0);
    } else if (event.type === 'operational_unavailability_changed') {
      setFiltersInitialized(false);
      setTimeout(() => setFiltersInitialized(true), 0);
    }
  }, [t]);

  // Connect rider WebSocket for non-admin users
  const shouldConnectRiderWs = userRole === 'user' && !!entityId;
  useRiderBookingWebSocket(entityId, shouldConnectRiderWs, handleRiderBookingEvent);

  // Fetch routes for filter dropdown
  const fetchRoutes = async () => {
    if (!entityId) {
      return;
    }

    try {
      const response = await axios.get(`/api/routes/${entityId}`);
      let fetchedRoutes = response.data || [];

      if (shouldTranslateContent && fetchedRoutes.length > 0) {
        const routeItems = fetchedRoutes.map(route => ({
          id: route.id,
          fields: {
            name: route.name || ''
          }
        }));

        try {
          const routeTranslations = await translationService.batchGetTranslations(
            entityId,
            'route',
            routeItems,
            i18n.language
          );

          fetchedRoutes = fetchedRoutes.map(route => {
            const translated = routeTranslations.get(route.id);
            return {
              ...route,
              name: translated?.name?.content || route.name
            };
          });
        } catch (translationError) {
          console.warn('Failed to translate route names for filter dropdown:', translationError);
        }
      }

      setRoutes(fetchedRoutes);
    } catch (error) {
      console.error('Error fetching routes:', error);
    }
  };

  // Fetch bookings (list view)
  const fetchBookings = async () => {
    const requestId = ++latestBookingRequestRef.current;
    setLoading(true);

    if (!entityId) {
      return;
    }

    try {

      // Choose endpoint based on role
      const endpoint = isAdminUser
        ? `/api/bookings/${entityId}/admin/all`
        : `/api/bookings/${entityId}/user`;

      // Build params, excluding empty values
      const params = {
        page: currentPage,
        limit: pageSize
      };

      // Only include status if not 'all'
      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
      }

      // Only include non-empty filter values
      if (filters.routeId) params.routeId = filters.routeId;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.startTime) params.startTime = filters.startTime;
      if (filters.endTime) params.endTime = filters.endTime;
      if (filters.search) params.search = filters.search;

      const response = await axios.get(endpoint, { params });

      // Ignore stale responses if a newer request has started
      if (latestBookingRequestRef.current !== requestId) {
        return;
      }

      // Extract bookings from response - unified format for both users and admins
      // Backend now returns { bookings, pagination } for both user and admin endpoints
      let fetchedBookings = isAdminUser
        ? response.data.data.bookings
        : response.data.bookings;

      if (!Array.isArray(fetchedBookings)) {
        fetchedBookings = [];
      }

      // Backends joins can occasionally return duplicate rows for the same booking
      // (e.g., when multiple trip rows match the same schedule/time). Deduplicate
      // client side so riders do not see duplicated cards while still preferring
      // the row that contains the richer trip metadata when available.
      const dedupedBookings = [];
      const bookingIndexMap = new Map();

      fetchedBookings.forEach(booking => {
        if (!bookingIndexMap.has(booking.id)) {
          bookingIndexMap.set(booking.id, dedupedBookings.length);
          dedupedBookings.push(booking);
          return;
        }

        const existingIndex = bookingIndexMap.get(booking.id);
        const existing = dedupedBookings[existingIndex];

        const existingHasTrip = Boolean(existing?.trip_id);
        const newHasTrip = Boolean(booking?.trip_id);

        if (!existingHasTrip && newHasTrip) {
          dedupedBookings[existingIndex] = booking;
        }
      });

      fetchedBookings = dedupedBookings;

      // Sort by ID descending so newest bookings appear first
      fetchedBookings.sort((a, b) => b.id - a.id);

      setBookings(fetchedBookings);

      // Set total unfiltered count and pagination data for both roles
      const pagination = isAdminUser
        ? response.data.data.pagination
        : response.data.pagination;

      if (pagination && pagination.total !== undefined) {
        setTotalUnfilteredCount(pagination.total);
        setTotalPages(Math.ceil(pagination.total / pageSize));
      } else {
        setTotalUnfilteredCount(fetchedBookings.length);
        setTotalPages(1);
      }

      // Fetch translations if multi-language is enabled
      if (shouldTranslateContent && fetchedBookings.length > 0) {
        await fetchTranslations(fetchedBookings, requestId);
      } else if (latestBookingRequestRef.current === requestId) {
        setTranslationsLoading(false);
      }
    } catch (error) {
      if (latestBookingRequestRef.current === requestId) {
        console.error('Error fetching bookings:', error);
        toast.error(t('Failed to fetch bookings'));
      }
    } finally {
      if (latestBookingRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  const handleAdminBookingCreated = () => {
    setViewMode('list');
    setCurrentPage(1);
    fetchBookings();
  };

  // Fetch trips grouped by trip (trip view)
  const fetchTripsGrouped = async () => {
    const requestId = ++latestTripRequestRef.current;
    setLoading(true);

    try {

      // Build params, excluding empty values
      const params = {
        page: currentPage,
        limit: pageSize
      };

      // Only include status if not 'all'
      if (filters.status && filters.status !== 'all') {
        params.status = filters.status;
      }

      // Only include non-empty filter values
      if (filters.routeId) params.routeId = filters.routeId;
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.startTime) params.startTime = filters.startTime;
      if (filters.endTime) params.endTime = filters.endTime;
      if (filters.search) params.search = filters.search;

      const response = await axios.get(`/api/bookings/${entityId}/by-trip`, { params });

      if (latestTripRequestRef.current !== requestId) {
        return;
      }

      let fetchedTrips = response.data.data.trips || [];

      setTrips(fetchedTrips);

      // Set total unfiltered count and pagination data
      if (response.data.data.pagination && response.data.data.pagination.total !== undefined) {
        setTotalUnfilteredCount(response.data.data.pagination.total);
        setTotalPages(Math.ceil(response.data.data.pagination.total / pageSize));
      } else {
        setTotalUnfilteredCount(fetchedTrips.length);
        setTotalPages(1);
      }

      // Fetch translations if multi-language is enabled
      if (shouldTranslateContent && fetchedTrips.length > 0) {
        await fetchTripTranslations(fetchedTrips, requestId);
      } else if (latestTripRequestRef.current === requestId) {
        setTranslationsLoading(false);
      }
    } catch (error) {
      if (latestTripRequestRef.current === requestId) {
        console.error('Error fetching trips:', error);
        toast.error(t('Failed to fetch bookings'));
      }
    } finally {
      if (latestTripRequestRef.current === requestId) {
        setLoading(false);
      }
    }
  };

  // Batch fetch translations for bookings
  const fetchTranslations = async (fetchedBookings, requestId) => {
    if (latestBookingRequestRef.current !== requestId) {
      return;
    }

    if (!shouldTranslateContent) {
      setTranslationsLoading(false);
      return;
    }

    setTranslationsLoading(true);
    try {
      // Extract unique route and waypoint IDs
      const uniqueRouteIds = [...new Set(fetchedBookings.map(b => b.route_id).filter(Boolean))];
      const uniqueWaypointIds = [
        ...new Set(
          fetchedBookings
            .flatMap(b => [b.pickup_stop_id, b.dropoff_stop_id])
            .filter(id => id !== undefined && id !== null)
        )
      ];

      // Prepare translation items
      const routeItems = uniqueRouteIds.map(routeId => ({
        id: routeId,
        fields: {
          name: fetchedBookings.find(b => b.route_id === routeId)?.route_name || ''
        }
      }));

      const waypointItems = uniqueWaypointIds.map(waypointId => {
        const booking = fetchedBookings.find(
          b => b.pickup_stop_id === waypointId || b.dropoff_stop_id === waypointId
        );
        const isPickup = booking?.pickup_stop_id === waypointId;
        return {
          id: waypointId,
          fields: {
            name: isPickup ? booking?.pickup_name || '' : booking?.dropoff_name || '',
            address: isPickup ? booking?.pickup_address || '' : booking?.dropoff_address || ''
          }
        };
      });

      // Batch fetch translations
      const [routeTranslations, waypointTranslations] = await Promise.all([
        translationService.batchGetTranslations(entityId, 'route', routeItems, i18n.language),
        waypointItems.length > 0
          ? translationService.batchGetTranslations(entityId, 'waypoint', waypointItems, i18n.language)
          : Promise.resolve(new Map())
      ]);

      // Apply translations to bookings
      const translatedBookings = fetchedBookings.map(booking => {
        const routeTranslation = routeTranslations.get(booking.route_id);
        const pickupTranslation = waypointTranslations.get(booking.pickup_stop_id);
        const dropoffTranslation = waypointTranslations.get(booking.dropoff_stop_id);

        return {
          ...booking,
          route_name: routeTranslation?.name?.content || booking.route_name,
          pickup_name: pickupTranslation?.name?.content || booking.pickup_name,
          pickup_address: pickupTranslation?.address?.content || booking.pickup_address,
          dropoff_name: dropoffTranslation?.name?.content || booking.dropoff_name,
          dropoff_address: dropoffTranslation?.address?.content || booking.dropoff_address
        };
      });

      if (latestBookingRequestRef.current === requestId) {
        setBookings(translatedBookings);
      }
    } catch (error) {
      console.warn('Failed to fetch translations:', error);
    } finally {
      if (latestBookingRequestRef.current === requestId) {
        setTranslationsLoading(false);
      }
    }
  };

  // Batch fetch translations for trips
  const fetchTripTranslations = async (fetchedTrips, requestId) => {
    if (latestTripRequestRef.current !== requestId) {
      return;
    }

    if (!shouldTranslateContent) {
      setTranslationsLoading(false);
      return;
    }

    setTranslationsLoading(true);
    try {
      // Extract all unique route and waypoint IDs from trips and their bookings
      const uniqueRouteIds = [...new Set(fetchedTrips.map(t => t.route_id).filter(Boolean))];
      const allBookings = fetchedTrips.flatMap(t => t.bookings || []);
      const uniqueWaypointIds = [
        ...new Set(
          allBookings
            .flatMap(b => [b.pickup_stop_id, b.dropoff_stop_id])
            .filter(id => id !== undefined && id !== null)
        )
      ];

      // Prepare translation items
      const routeItems = uniqueRouteIds.map(routeId => ({
        id: routeId,
        fields: {
          name: fetchedTrips.find(t => t.route_id === routeId)?.route_name || ''
        }
      }));

      const waypointItems = uniqueWaypointIds.map(waypointId => {
        const booking = allBookings.find(
          b => b.pickup_stop_id === waypointId || b.dropoff_stop_id === waypointId
        );
        const isPickup = booking?.pickup_stop_id === waypointId;
        return {
          id: waypointId,
          fields: {
            name: isPickup ? booking?.pickup_name || '' : booking?.dropoff_name || '',
            address: isPickup ? booking?.pickup_address || '' : booking?.dropoff_address || ''
          }
        };
      });

      // Batch fetch translations
      const [routeTranslations, waypointTranslations] = await Promise.all([
        translationService.batchGetTranslations(entityId, 'route', routeItems, i18n.language),
        waypointItems.length > 0
          ? translationService.batchGetTranslations(entityId, 'waypoint', waypointItems, i18n.language)
          : Promise.resolve(new Map())
      ]);

      // Apply translations to trips and their bookings
      const translatedTrips = fetchedTrips.map(trip => {
        const routeTranslation = routeTranslations.get(trip.route_id);

        // Translate bookings within the trip
        const translatedBookings = (trip.bookings || []).map(booking => {
          const pickupTranslation = waypointTranslations.get(booking.pickup_stop_id);
          const dropoffTranslation = waypointTranslations.get(booking.dropoff_stop_id);

          return {
            ...booking,
            pickup_name: pickupTranslation?.name?.content || booking.pickup_name,
            pickup_address: pickupTranslation?.address?.content || booking.pickup_address,
            dropoff_name: dropoffTranslation?.name?.content || booking.dropoff_name,
            dropoff_address: dropoffTranslation?.address?.content || booking.dropoff_address
          };
        });

        return {
          ...trip,
          route_name: routeTranslation?.name?.content || trip.route_name,
          bookings: translatedBookings
        };
      });

      if (latestTripRequestRef.current === requestId) {
        setTrips(translatedTrips);
      }
    } catch (error) {
      console.warn('Failed to fetch trip translations:', error);
    } finally {
      if (latestTripRequestRef.current === requestId) {
        setTranslationsLoading(false);
      }
    }
  };

  // Handle admin action requests by opening the confirmation dialog
  const handleAdminActionRequest = (action, bookingDetails) => {
    if (!bookingDetails || !bookingDetails.id) {
      return;
    }

    setPendingAction({
      type: action,
      booking: bookingDetails
    });
  };

  // Execute booking status changes after the user confirms the dialog
  const performBookingAction = async (action, bookingDetails) => {
    const bookingId = bookingDetails?.id;
    if (!bookingId) {
      return;
    }

    try {
      if (action === 'confirm') {
        await axios.put(`/api/bookings/${entityId}/admin/${bookingId}/status`, {
          status: 'confirmed',
          ...(bookingDetails.tripId && { tripId: bookingDetails.tripId })
        });
        toast.success(t('bookingStatusUpdatedSuccessfully'));
      } else if (action === 'cancel') {
        await axios.put(`/api/bookings/${entityId}/admin/${bookingId}/cancel`, {
          cancellationReason: t('adminCancelledBookingReason'),
          notifyUser: true,
          ...(bookingDetails.tripId && { tripId: bookingDetails.tripId })
        });
        toast.success(t('bookingCanceledSuccessfully'));
      }

      // Refresh data so the latest status is reflected in the UI
      if (viewMode === 'list') {
        await fetchBookings();
      } else {
        await fetchTripsGrouped();
      }
    } catch (error) {
      console.error('Error updating booking:', error);
      toast.error(t('Failed to update booking status'));
      throw error;
    }
  };

  // Run the queued action, keeping the dialog open if a failure occurs
  const handleActionConfirm = async () => {
    if (!pendingAction) {
      return;
    }

    setActionProcessing(true);
    try {
      await performBookingAction(pendingAction.type, pendingAction.booking);
      setPendingAction(null);
    } catch {
      // Allow the admin to retry or close the dialog after a failure
    } finally {
      setActionProcessing(false);
    }
  };

  const handleActionDialogClose = () => {
    if (actionProcessing) {
      return;
    }
    setPendingAction(null);
  };

  // Handle booking cancellation for users
  const handleUserCancelBooking = async (bookingId) => {
    try {
      await axios.post(`/api/bookings/${entityId}/${bookingId}/cancel`);
      toast.success(t('bookingCanceledSuccessfully'));

      // Update local state
      setBookings(prevBookings =>
        prevBookings.map(booking =>
          booking.id === bookingId
            ? { ...booking, status: 'canceled' }
            : booking
        )
      );
    } catch (error) {
      console.error('Error cancelling booking:', error);
      toast.error(t('Failed to cancel booking'));
    }
  };

  return (
    <div className={cn('space-y-6', background.primary)}>
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className={cn('text-2xl font-bold', text.primary)}>
          {isAdminUser ? t('allBookings') : t('myBookings')}
        </h2>

        {/* Export Button - Admin Only 
        {isAdminUser && (
          <button
            onClick={handleExport}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
          >
            <Download className="w-4 h-4" />
            {t('Export')}
          </button>
        )}*/}
      </div>

      {/* View Toggle - Only show for admins (riders don't need trip-grouped view) */}
      {isAdminUser && (
        <div className={cn('grid w-full grid-cols-3 gap-1 rounded-lg p-1 sm:inline-grid sm:w-auto', background.secondary)}>
          <button
            type="button"
            onClick={() => setViewMode('list')}
            aria-pressed={viewMode === 'list'}
            className={cn(
              'flex min-h-10 min-w-0 items-center justify-center gap-2 rounded-md px-2 py-2 text-sm font-medium leading-tight transition-colors sm:px-4',
              viewMode === 'list'
                ? 'bg-blue-600 text-white'
                : cn('hover:bg-gray-100 dark:hover:bg-gray-700', text.primary)
            )}
          >
            <List className="h-4 w-4 flex-shrink-0" />
            <span className="min-w-0 break-words text-center">{t('listView')}</span>
          </button>
          <button
            type="button"
            onClick={() => setViewMode('trip')}
            aria-pressed={viewMode === 'trip'}
            className={cn(
              'flex min-h-10 min-w-0 items-center justify-center gap-2 rounded-md px-2 py-2 text-sm font-medium leading-tight transition-colors sm:px-4',
              viewMode === 'trip'
                ? 'bg-blue-600 text-white'
                : cn('hover:bg-gray-100 dark:hover:bg-gray-700', text.primary)
            )}
          >
            <Calendar className="h-4 w-4 flex-shrink-0" />
            <span className="min-w-0 break-words text-center">{t('byTrip')}</span>
          </button>
          <button
            type="button"
            onClick={() => setViewMode('create')}
            aria-pressed={viewMode === 'create'}
            className={cn(
              'flex min-h-10 min-w-0 items-center justify-center gap-2 rounded-md px-2 py-2 text-sm font-medium leading-tight transition-colors sm:px-4',
              viewMode === 'create'
                ? 'bg-blue-600 text-white'
                : cn('hover:bg-gray-100 dark:hover:bg-gray-700', text.primary)
            )}
          >
            <Plus className="h-4 w-4 flex-shrink-0" />
            <span className="min-w-0 break-words text-center">{t('createBooking')}</span>
          </button>
        </div>
      )}

      {/* Filters - Only show in list and trip views, not in create booking view */}
      {viewMode !== 'create' && (
        <BookingFilters
          userRole={userRole}
          filters={filters}
          onFilterChange={setFilters}
          onInitialized={() => setFiltersInitialized(true)}
          preferredDateFormat={preferredDateFormat}
          userLanguage={preferredLanguage}
          routes={routes}
          showSearch={isAdminUser}
          totalBookings={totalUnfilteredCount}
          filterThreshold={0} // Always show filters - users need access to change date ranges
          bookingWindowDays={bookingWindowDays}
        />
      )}

      {/* Translation Loading Indicator */}
      {translationsLoading && (
        <div className={cn('text-sm', text.secondary)}>
          {t('translating')}...
        </div>
      )}

      {/* Pagination Controls - Only show in list and trip views, not in create booking view */}
      {viewMode !== 'create' && !loading && totalUnfilteredCount > 0 && (
        <div className={cn('flex justify-between items-center px-4 py-3 rounded-lg', background.secondary)}>
          <div className={cn('text-sm', text.secondary)}>
            {t('paginationInfo', {
              start: Math.min((currentPage - 1) * pageSize + 1, totalUnfilteredCount),
              end: Math.min(currentPage * pageSize, totalUnfilteredCount),
              total: totalUnfilteredCount
            })}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className={cn(
                  'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                  currentPage === 1
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                    : 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800'
                )}
              >
                {t('First')}
              </button>

              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className={cn(
                  'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                  currentPage === 1
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                    : 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800'
                )}
              >
                {t('Previous')}
              </button>

              <span className={cn('px-4 py-1.5 text-sm font-medium', text.primary)}>
                {t('pageInfo', { current: currentPage, total: totalPages })}
              </span>

              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className={cn(
                  'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                  currentPage === totalPages
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                    : 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800'
                )}
              >
                {t('Next')}
              </button>

              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className={cn(
                  'px-3 py-1.5 rounded-md text-sm font-medium transition-colors',
                  currentPage === totalPages
                    ? 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                    : 'bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800'
                )}
              >
                {t('Last')}
              </button>
            </div>
          )}
        </div>
      )}

      {isAdminUser && viewMode !== 'create' && (
        <ActiveDriversPanel
          entityId={entityId}
          refreshKey={activeDriversRefreshKey}
          preferredDateFormat={preferredDateFormat}
          preferredTimeFormat={preferredTimeFormat}
        />
      )}

      {/* Content - List View */}
      {viewMode === 'list' && (
        <>
          {isAdminUser ? (
            // Admin: Table view with all bookings
            <AdminBookingTable
              bookings={bookings}
              loading={loading}
              onBookingAction={handleAdminActionRequest}
              t={t}
              cn={cn}
              background={background}
              text={text}
              preferredDateFormat={preferredDateFormat}
              preferredTimeFormat={preferredTimeFormat}
              bookingVerificationEnabled={bookingVerificationEnabled}
              verificationPrompt={translatedVerificationPrompt}
              newBookingIds={newBookingIds}
              hideRouteColumn={routes.length === 1}
            />
          ) : (
            // User: Card-based view
            <BookingListPanel
              bookings={bookings}
              onSelect={() => {}} // No selection needed in view-only mode
              onCancel={handleUserCancelBooking}
              routes={routes}
              entityId={entityId}
              referenceDataMap={{}} // Can pass reference data if needed for ETA
              preferredDateFormat={preferredDateFormat}
              preferredTimeFormat={preferredTimeFormat}
              bookingVerificationEnabled={bookingVerificationEnabled}
              boardingAvailable={entitySettings?.boarding_tracking_enabled}
              onViewTrip={handleViewTrip}
            />
          )}
        </>
      )}

      {/* Content - Trip View (Admin only) */}
      {viewMode === 'trip' && isAdminUser && (
        <TripGroupedView
          trips={trips}
          userRole={userRole}
          onBookingAction={handleAdminActionRequest}
          onRefreshTrips={fetchTripsGrouped}
          loading={loading}
          preferredDateFormat={preferredDateFormat}
          preferredTimeFormat={preferredTimeFormat}
          entityId={entityId}
          bookingVerificationEnabled={bookingVerificationEnabled}
          verificationPrompt={translatedVerificationPrompt}
        />
      )}

      {/* Content - Create Booking View (Admin only) */}
      {viewMode === 'create' && isAdminUser && (
        renderCreateForm ? (
          <div className="mt-6">
            {renderCreateForm({
              onSuccess: handleAdminBookingCreated,
              embedded: true,
              isAdminBooking: true
            })}
          </div>
        ) : (
          <div className={cn('p-6 rounded-lg', background.secondary)}>
            <div className="text-center py-12">
              <h3 className={cn('text-xl font-semibold mb-4', text.primary)}>
                {t('createBooking')}
              </h3>
              <p className={cn('text-sm mb-6', text.secondary)}>
                {t('adminBookingCreationPlaceholder', 'Admin booking creation form will be implemented here')}
              </p>
              <div className={cn('text-sm', text.secondary)}>
                {t('adminBookingCreationInstructions', 'This feature requires additional implementation. See docs/ADMIN_BOOKING_CREATION.md for details.')}
              </div>
            </div>
          </div>
        )
      )}

      {pendingAction && (
        <ConfirmDialogShell
          isOpen={Boolean(pendingAction)}
          onClose={handleActionDialogClose}
          onConfirm={handleActionConfirm}
          title={isConfirmAction ? t('confirmBooking') : t('cancelBooking')}
          confirmLabel={isConfirmAction ? t('confirm') : t('confirmCancel')}
          cancelLabel={t('cancel')}
          confirmVariant={isConfirmAction ? 'success' : 'danger'}
          confirmIcon={isConfirmAction ? CheckCircle2 : AlertTriangle}
          cancelIcon={XCircle}
          isLoading={actionProcessing}
          isDisabled={actionProcessing}
          showIcon
          iconVariant={isConfirmAction ? 'success' : 'danger'}
        >
          {/* Provide booking context so admins can double-check before proceeding */}
          <div className="space-y-3">
            <p className={cn(text.primary)}>
              {isConfirmAction
                ? t('confirmBookingActionMessage', { bookingId: pendingAction.booking.id })
                : t('cancelBookingActionMessage', { bookingId: pendingAction.booking.id })}
            </p>

              {pendingAction.booking.routeName && (
                <div className={cn('text-sm', text.secondary)}>
                  <span className="font-medium">{t('route')}:</span> {pendingAction.booking.routeName}
                </div>
              )}

              {pendingAction.booking.tripDate && (
                <div className={cn('text-sm', text.secondary)}>
                  <span className="font-medium">{t('date')}:</span> {formatDate(pendingAction.booking.tripDate, preferredDateFormat)}
                </div>
              )}

              {pendingAction.booking.tripTime && (
                <div className={cn('text-sm', text.secondary)}>
                  <span className="font-medium">{t('time')}:</span> {formatTimeString(pendingAction.booking.tripTime, preferredTimeFormat)}
                </div>
              )}

            {pendingAction.booking.passengers !== undefined && pendingAction.booking.passengers !== null && (
              <div className={cn('text-sm', text.secondary)}>
                <span className="font-medium">{t('passengers')}:</span> {pendingAction.booking.passengers}
              </div>
            )}

            {pendingAction.booking.contactNumber && (
              <div className={cn('text-sm', text.secondary)}>
                <span className="font-medium">{t('contact')}:</span> {pendingAction.booking.contactNumber}
              </div>
            )}

            {pendingAction.booking.requestAccessibleVehicle && (
              <div className={cn('text-sm', text.secondary)}>
                <span className="font-medium">{t('accessibleVehicleRequested')}:</span> {t('yes')}
              </div>
            )}
          </div>
        </ConfirmDialogShell>
      )}
    </div>
  );
};

// Simple admin booking table component (inline)
const AdminBookingTable = ({
  bookings,
  loading,
  onBookingAction,
  t,
  cn,
  background,
  text,
  preferredDateFormat,
  preferredTimeFormat,
  bookingVerificationEnabled = false,
  newBookingIds = new Set(),
  hideRouteColumn = false
}) => {
  // Sort state - default to ID descending (newest first)
  const [sortColumn, setSortColumn] = useState('id');
  const [sortDirection, setSortDirection] = useState('desc');
  const displayBookings = Array.isArray(bookings) ? bookings : [];
  const getBookingDisplayDate = (booking) =>
    booking?.booking_type === 'recurring'
      ? (booking.next_occurrence_date || booking.trip_date || booking.start_date)
      : (booking?.start_date || booking?.trip_date);
  const getRecurringProgressText = (booking) => {
    if (booking?.booking_type !== 'recurring') {
      return null;
    }

    const totalOccurrences = Number(booking.total_occurrences);
    const completedOccurrences = Number(booking.completed_occurrences);

    if (Number.isFinite(totalOccurrences) && totalOccurrences > 0) {
      const safeCompletedOccurrences = Number.isFinite(completedOccurrences) ? completedOccurrences : 0;
      const progressKey =
        safeCompletedOccurrences > 0 && safeCompletedOccurrences < totalOccurrences
          ? 'recurringPartialProgress'
          : 'recurringProgress';

      return `${t('recurringSeries')} · ${t(progressKey, {
        completed: safeCompletedOccurrences,
        total: totalOccurrences
      })}`;
    }

    return t('recurringSeries');
  };
  const hasSpecialRequests = displayBookings.some(booking =>
    normalizeTableText(booking.special_requests) !== ''
  );
  const hiddenSortColumn =
    (hideRouteColumn && sortColumn === 'route') ||
    (!hasSpecialRequests && sortColumn === 'specialRequests');
  const effectiveSortColumn = hiddenSortColumn
    ? 'id'
    : sortColumn;
  const effectiveSortDirection = hiddenSortColumn
    ? 'desc'
    : sortDirection;

  // A hidden optional column must not continue controlling row order after filters refresh.
  useEffect(() => {
    if ((hideRouteColumn && sortColumn === 'route') || (!hasSpecialRequests && sortColumn === 'specialRequests')) {
      setSortColumn('id');
      setSortDirection('desc');
    }
  }, [hasSpecialRequests, hideRouteColumn, sortColumn]);

  // Handle column header click
  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      // New column - default to ascending (except ID which defaults desc)
      setSortColumn(column);
      setSortDirection(column === 'id' ? 'desc' : 'asc');
    }
  };

  // Sort indicator component
  const SortIndicator = ({ column }) => {
    if (effectiveSortColumn !== column) {
      return <span className="ml-1 text-gray-400">↕</span>;
    }
    return <span className="ml-1">{effectiveSortDirection === 'asc' ? '▲' : '▼'}</span>;
  };

  // Sortable header cell
  const SortableHeader = ({ column, children }) => (
    <th
      className={cn(
        'px-6 py-3 text-left text-xs font-medium uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors',
        text.primary
      )}
      onClick={() => handleSort(column)}
    >
      <span className="flex items-center">
        {children}
        <SortIndicator column={column} />
      </span>
    </th>
  );

  // Sort bookings based on current sort state
  const sortedBookings = [...displayBookings].sort((a, b) => {
    let aVal, bVal;

    switch (effectiveSortColumn) {
      case 'id':
        aVal = a.id;
        bVal = b.id;
        break;
      case 'rider':
        aVal = (a.user_name || '').toLowerCase();
        bVal = (b.user_name || '').toLowerCase();
        break;
      case 'route':
        aVal = (a.route_name || '').toLowerCase();
        bVal = (b.route_name || '').toLowerCase();
        break;
      case 'pickup':
        aVal = normalizeTableText(a.pickup_name).toLowerCase();
        bVal = normalizeTableText(b.pickup_name).toLowerCase();
        break;
      case 'dropoff':
        aVal = normalizeTableText(a.dropoff_name).toLowerCase();
        bVal = normalizeTableText(b.dropoff_name).toLowerCase();
        break;
      case 'date':
        // Sort by date, then by time
        aVal = `${getBookingDisplayDate(a) || ''} ${a.trip_time || ''}`;
        bVal = `${getBookingDisplayDate(b) || ''} ${b.trip_time || ''}`;
        break;
      case 'specialRequests':
        aVal = normalizeTableText(a.special_requests).toLowerCase();
        bVal = normalizeTableText(b.special_requests).toLowerCase();
        break;
      case 'status':
        aVal = (a.status || '').toLowerCase();
        bVal = (b.status || '').toLowerCase();
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return effectiveSortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return effectiveSortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Prepare contextual booking data so the confirmation dialog can display details
  const buildActionDetails = (booking) => ({
    id: booking.id,
    userName: booking.user_name,
    routeName: booking.route_name,
    tripDate: getBookingDisplayDate(booking),
    tripTime: booking.trip_time,
    passengers: booking.riders ?? booking.passengers ?? null,
    contactNumber: booking.contact_number,
    requestAccessibleVehicle: isEnabledFlag(booking.request_accessible_vehicle)
  });

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, index) => (
          <div key={index} className="animate-pulse">
            <div className="h-16 bg-gray-200 rounded-lg dark:bg-gray-700" />
          </div>
        ))}
      </div>
    );
  }

  if (displayBookings.length === 0) {
    return (
      <div className={cn('text-center py-12', text.secondary)}>
        <p className="text-lg">{t('noBookingsFound')}</p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className={cn('min-w-full rounded-lg shadow', background.secondary)}>
        <thead className={cn('border-b', 'border-gray-200 dark:border-gray-700')}>
          <tr>
            <SortableHeader column="id">{t('id')}</SortableHeader>
            <SortableHeader column="rider">{t('Rider')}</SortableHeader>
            {!hideRouteColumn && <SortableHeader column="route">{t('route')}</SortableHeader>}
            <SortableHeader column="pickup">{t('From')}</SortableHeader>
            <SortableHeader column="dropoff">{t('To')}</SortableHeader>
            <SortableHeader column="date">{t('date')} / {t('time')}</SortableHeader>
            {bookingVerificationEnabled && (
              <th className={cn('px-6 py-3 text-left text-xs font-medium uppercase tracking-wider', text.primary)}>
                {t('verificationInfo')}
              </th>
            )}
            {hasSpecialRequests && (
              <SortableHeader column="specialRequests">{t('specialRequests')}</SortableHeader>
            )}
            <SortableHeader column="status">{t('status')}</SortableHeader>
            <th className={cn('px-6 py-3 text-left text-xs font-medium uppercase tracking-wider', text.primary)}>
              {t('Actions')}
            </th>
          </tr>
        </thead>
        <tbody className={cn('divide-y', 'divide-gray-200 dark:divide-gray-700')}>
          {sortedBookings.map((booking, index) => {
            const rowBackground = index % 2 === 0 ? background.primary : background.secondary;
            const pickupName = normalizeTableText(booking.pickup_name);
            const dropoffName = normalizeTableText(booking.dropoff_name);
            const specialRequestText = normalizeTableText(booking.special_requests);
            const recurringProgressText = getRecurringProgressText(booking);
            return (
              <tr
                key={booking.id}
                className={cn(
                  rowBackground,
                  'transition-colors duration-150',
                  'hover:bg-gray-100 dark:hover:bg-gray-700/80'
                )}
              >
              <td className={cn('px-6 py-4 whitespace-nowrap text-sm', text.primary)}>
                <span className="flex items-center gap-2">
                  #{booking.id}
                  {newBookingIds.has(booking.id) && (
                    <span className="px-1.5 py-0.5 text-xs font-semibold rounded bg-blue-500 text-white animate-pulse">
                      {t('new')}
                    </span>
                  )}
                </span>
              </td>
              <td className={cn('px-6 py-4 whitespace-nowrap', text.primary)}>
                <div className="text-sm">{booking.user_name}</div>
                <div className={cn('text-sm', text.secondary)}>{booking.contact_number}</div>
              </td>
              {!hideRouteColumn && (
                <td className={cn('px-6 py-4 whitespace-nowrap text-sm', text.primary)}>
                  <div>{booking.route_name}</div>
                  {isEnabledFlag(booking.request_accessible_vehicle) && (
                    <div className={cn('text-xs mt-1', text.secondary)}>
                      {t('accessibleVehicleRequested')}
                    </div>
                  )}
                </td>
              )}
              <td className={cn('px-6 py-4 whitespace-nowrap text-sm', text.primary)}>
                <div className="max-w-xs truncate" title={pickupName}>
                  {pickupName || '-'}
                </div>
              </td>
              <td className={cn('px-6 py-4 whitespace-nowrap text-sm', text.primary)}>
                <div className="max-w-xs truncate" title={dropoffName}>
                  {dropoffName || '-'}
                </div>
              </td>
              <td className={cn('px-6 py-4 whitespace-nowrap', text.primary)}>
                <div className="text-sm">
                  {formatDate(getBookingDisplayDate(booking), preferredDateFormat)}
                </div>
                <div className={cn('text-sm', text.secondary)}>
                  {formatTimeString(booking.trip_time, preferredTimeFormat)}
                </div>
              </td>
              {bookingVerificationEnabled && (
                <td className={cn('px-6 py-4', text.primary)}>
                  <div className="text-sm max-w-xs truncate" title={booking.entity_verification_value || ''}>
                    {booking.entity_verification_value || '-'}
                  </div>
                </td>
              )}
              {hasSpecialRequests && (
                <td className={cn('px-6 py-4 text-sm', text.primary)}>
                  <div className="max-w-xs truncate" title={specialRequestText}>
                    {specialRequestText || '-'}
                  </div>
                </td>
              )}
              <td className="px-6 py-4 whitespace-nowrap">
                <span
                  className={cn(
                    'px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full',
                    booking.status === 'confirmed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                    booking.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                    booking.status === 'waitlist' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                    booking.status === 'canceled' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                    'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                  )}
                >
                  {t(`bookingStatus.${booking.status}`)}
                </span>
                {recurringProgressText && (
                  <div className={cn('text-xs mt-1', text.secondary)}>
                    {recurringProgressText}
                  </div>
                )}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div className="flex items-center gap-2">
                  {booking.status === 'pending' && (
                    <>
                      <button
                        onClick={() => onBookingAction && onBookingAction('confirm', buildActionDetails(booking))}
                        className={cn(
                          'px-3 py-1.5 rounded-md text-sm font-medium transition-colors shadow-sm',
                          'bg-green-600 text-white hover:bg-green-700 hover:shadow-md',
                          'dark:bg-green-700 dark:hover:bg-green-800'
                        )}
                      >
                        {t('confirm')}
                      </button>
                      <button
                        onClick={() => onBookingAction && onBookingAction('cancel', buildActionDetails(booking))}
                        className={cn(
                          'px-3 py-1.5 rounded-md text-sm font-medium transition-colors shadow-sm',
                          'bg-red-600 text-white hover:bg-red-700 hover:shadow-md',
                          'dark:bg-red-700 dark:hover:bg-red-800'
                        )}
                      >
                        {t('cancel')}
                      </button>
                    </>
                  )}
                  {booking.status === 'confirmed' && (
                    <button
                      onClick={() => onBookingAction && onBookingAction('cancel', buildActionDetails(booking))}
                      className={cn(
                        'px-3 py-1.5 rounded-md text-sm font-medium transition-colors shadow-sm',
                        'bg-red-600 text-white hover:bg-red-700 hover:shadow-md',
                        'dark:bg-red-700 dark:hover:bg-red-800'
                      )}
                    >
                      {t('cancel')}
                    </button>
                  )}
                  {/* Waitlist bookings: awaiting route optimization before confirmation */}
                  {booking.status === 'waitlist' && (
                    <button
                      onClick={() => onBookingAction && onBookingAction('cancel', buildActionDetails(booking))}
                      className={cn(
                        'px-3 py-1.5 rounded-md text-sm font-medium transition-colors shadow-sm',
                        'bg-red-600 text-white hover:bg-red-700 hover:shadow-md',
                        'dark:bg-red-700 dark:hover:bg-red-800'
                      )}
                    >
                      {t('cancel')}
                    </button>
                  )}
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default BookingView;
